import moment from "moment";
import {
  getFormOrganizationDisplayName,
  normalizeFormStatus,
  normalizeFormsListResponse,
  normalizeFormType,
} from "../../../utils/formTabs";

export const buildSurveyLink = (formId) =>
  `${window.location.origin}/form-submissions/${formId}`;

export const extractFormIdFromSurveyLink = (surveyLink) => {
  if (!surveyLink) {
    return null;
  }

  try {
    const url = new URL(String(surveyLink), window.location.origin);
    const segments = url.pathname.split("/").filter(Boolean);

    return normalizeMongoId(segments[segments.length - 1]);
  } catch {
    const segments = String(surveyLink).split("/").filter(Boolean);

    return normalizeMongoId(segments[segments.length - 1]);
  }
};

export const getPanelUserEmail = (panelUser) =>
  String(
    panelUser?.panelUser?.email ||
      panelUser?.user?.email ||
      panelUser?.email ||
      panelUser?.user?.email_address ||
      "",
  )
    .trim()
    .toLowerCase();

export const PANEL_LINK_MESSAGE_GREETING = "Dear [Recipient Full Name],";

export const DEFAULT_PANEL_LINK_MESSAGE_BODY =
  "You've been invited to complete a survey. Use the shared link below to open the form and submit your responses.";

export const DEFAULT_PANEL_LINK_HEADER_SUBTITLE = "We'd love to hear from you";

export const getActivePanelForms = (formsData, searchQuery = "") => {
  const forms = normalizeFormsListResponse(formsData, false).filter(
    (form) =>
      form.questions.length > 0 &&
      normalizeFormType(form.type) === "panel" &&
      normalizeFormStatus(form.status) === "active",
  );

  const query = String(searchQuery || "").trim().toLowerCase();

  if (!query) {
    return forms;
  }

  return forms.filter((form) => {
    const title = String(form.title || "").toLowerCase();
    const organization = String(
      getFormOrganizationDisplayName(form) || "",
    ).toLowerCase();

    return title.includes(query) || organization.includes(query);
  });
};

export const normalizePanelUsersListResponse = (panelUsers) => {
  const root = panelUsers?.data ?? panelUsers;

  if (!root) {
    return [];
  }

  if (Array.isArray(root)) {
    return root;
  }

  if (Array.isArray(root.data)) {
    return root.data;
  }

  if (Array.isArray(root.panel_users)) {
    return root.panel_users;
  }

  if (Array.isArray(root.panelUsers)) {
    return root.panelUsers;
  }

  if (Array.isArray(root.users)) {
    return root.users;
  }

  return [];
};

export const normalizeMongoId = (value) => {
  if (value == null || value === "") {
    return null;
  }

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (typeof value === "object") {
    return value.$oid || value.oid || value._id || value.id || null;
  }

  return null;
};

const PANEL_USER_DATE_FIELDS = [
  "date_created",
  "dateCreated",
  "joined_date",
  "joinedDate",
  "created_at",
  "createdAt",
  "created_on",
  "createdOn",
  "registration_date",
  "registrationDate",
  "timestamp",
];

const pickPanelUserDateValue = (source) => {
  if (!source || typeof source !== "object") {
    return null;
  }

  for (const key of PANEL_USER_DATE_FIELDS) {
    const value = source[key];

    if (value != null && value !== "") {
      return value;
    }
  }

  return null;
};

export const getDateFromMongoObjectId = (objectId) => {
  const id = normalizeMongoId(objectId);

  if (!id || !/^[a-f0-9]{24}$/i.test(id)) {
    return null;
  }

  const timestamp = parseInt(id.substring(0, 8), 16) * 1000;
  const date = new Date(timestamp);

  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateValue = (raw) => {
  if (raw == null || raw === "") {
    return null;
  }

  if (typeof raw === "object") {
    const nested =
      raw.$date ||
      raw.date ||
      raw.value ||
      raw.toISOString?.();

    if (nested) {
      if (nested instanceof Date) {
        return moment(nested).format("YYYY-MM-DD");
      }

      const parsed = moment(nested);
      return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
    }

    const objectIdDate = getDateFromMongoObjectId(raw.$oid || raw._id || raw.id);

    if (objectIdDate) {
      return moment(objectIdDate).format("YYYY-MM-DD");
    }
  }

  if (typeof raw === "number" || /^\d+$/.test(String(raw).trim())) {
    const numeric = Number(raw);
    const date =
      numeric < 1e12 ? new Date(numeric * 1000) : new Date(numeric);

    return Number.isNaN(date.getTime())
      ? null
      : moment(date).format("YYYY-MM-DD");
  }

  const parsed = moment(raw);

  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

const findDeepPanelUserDateValue = (source, depth = 0) => {
  if (!source || typeof source !== "object" || depth > 4) {
    return null;
  }

  for (const [key, value] of Object.entries(source)) {
    if (value == null || value === "") {
      continue;
    }

    if (/date|created|joined|timestamp|time/i.test(key)) {
      const formatted = formatDateValue(value);

      if (formatted) {
        return formatted;
      }
    }

    if (typeof value === "object" && !Array.isArray(value)) {
      const nested = findDeepPanelUserDateValue(value, depth + 1);

      if (nested) {
        return nested;
      }
    }
  }

  return null;
};

export const formatPanelUserJoinedDate = (panelUser) => {
  const raw =
    pickPanelUserDateValue(panelUser) ??
    pickPanelUserDateValue(panelUser?.user) ??
    pickPanelUserDateValue(panelUser?.panel_user);

  const formatted = formatDateValue(raw);

  if (formatted) {
    return formatted;
  }

  const deepFormatted = findDeepPanelUserDateValue(panelUser);

  if (deepFormatted) {
    return deepFormatted;
  }

  const objectIdDate =
    getDateFromMongoObjectId(panelUser?._id) ??
    getDateFromMongoObjectId(panelUser?.id) ??
    getDateFromMongoObjectId(panelUser?.user?._id) ??
    getDateFromMongoObjectId(panelUser?.user?.id);

  if (objectIdDate) {
    return moment(objectIdDate).format("YYYY-MM-DD");
  }

  return "N/A";
};

export const getPanelUserImageUrl = (panelUser) =>
  panelUser?.image ||
  panelUser?.avatar ||
  panelUser?.user?.image ||
  null;

export const comparePanelUsersAlphabetically = (a, b) => {
  const nameA = String(a?.panelUser?.name || "")
    .trim()
    .toLowerCase();
  const nameB = String(b?.panelUser?.name || "")
    .trim()
    .toLowerCase();
  const emailA = String(a?.panelUser?.email || "")
    .trim()
    .toLowerCase();
  const emailB = String(b?.panelUser?.email || "")
    .trim()
    .toLowerCase();
  const sortKeyA = nameA || emailA;
  const sortKeyB = nameB || emailB;

  return sortKeyA.localeCompare(sortKeyB, undefined, { sensitivity: "base" });
};

export const mapPanelUsersToTableData = (panelUsers) => {
  const panelUserData = normalizePanelUsersListResponse(panelUsers);

  return panelUserData
    .map((panelUser) => ({
      _id: normalizeMongoId(panelUser._id) || panelUser._id,
      panelUser: {
        name: panelUser.user?.full_name || panelUser.full_name,
        email:
          panelUser.user?.email ||
          panelUser.email ||
          panelUser.user?.email_address,
        avatar: getPanelUserImageUrl(panelUser),
      },
      status: panelUser.status || "active",
      assignedForms: panelUser.assigned_forms || [],
      submittedForms:
        panelUser.submitted_forms || panelUser.completed_forms || [],
      completedForms:
        panelUser.completed_forms || panelUser.submitted_forms || [],
      joinedDate: formatPanelUserJoinedDate(panelUser),
    }))
    .sort(comparePanelUsersAlphabetically);
};

export const normalizeFormIdList = (forms) => {
  if (!Array.isArray(forms)) {
    return [];
  }

  return forms
    .map(
      (item) =>
        normalizeMongoId(item) ||
        normalizeMongoId(item?._id) ||
        normalizeMongoId(item?.form_id) ||
        normalizeMongoId(item?.form),
    )
    .filter(Boolean);
};

export const getPanelUserFormConflict = (record, formId) => {
  const normalizedFormId = normalizeMongoId(formId);

  if (!normalizedFormId) {
    return null;
  }

  const assignedIds = normalizeFormIdList(record?.assignedForms);
  const submittedIds = normalizeFormIdList(
    record?.submittedForms || record?.completedForms,
  );

  if (submittedIds.includes(normalizedFormId)) {
    return "submitted";
  }

  if (assignedIds.includes(normalizedFormId)) {
    return "assigned";
  }

  return null;
};

export const PANEL_USER_FORM_CONFLICT_MESSAGES = {
  submitted: "This panel user has already submitted this form.",
};

export const checkSelectedPanelUsersForForm = (selectedRows, formId) => {
  const conflicts = [];
  const eligible = [];

  selectedRows.forEach((row) => {
    const conflict = getPanelUserFormConflict(row, formId);

    if (conflict) {
      conflicts.push({
        rowId: row._id,
        email: row.panelUser?.email,
        name: row.panelUser?.name,
        reason: conflict,
      });
      return;
    }

    eligible.push(row);
  });

  return { conflicts, eligible };
};

export const checkPanelUserEmailsForSurveyLink = (
  panelUsers,
  emails,
  surveyLink,
) => {
  const formId = extractFormIdFromSurveyLink(surveyLink);
  const panelUserData = normalizePanelUsersListResponse(panelUsers);
  const conflicts = [];
  const eligibleEmails = [];
  const eligibleUsers = [];

  emails.forEach((email) => {
    const normalizedEmail = String(email || "")
      .trim()
      .toLowerCase();

    if (!normalizedEmail) {
      return;
    }

    const panelUser = panelUserData.find(
      (user) => getPanelUserEmail(user) === normalizedEmail,
    );

    if (!panelUser) {
      eligibleEmails.push(email);
      eligibleUsers.push({
        rowId: null,
        email,
        name: email,
      });
      return;
    }

    const conflict = getPanelUserFormConflict(
      {
        assignedForms: panelUser.assigned_forms || [],
        submittedForms:
          panelUser.submitted_forms || panelUser.completed_forms || [],
        completedForms:
          panelUser.completed_forms || panelUser.submitted_forms || [],
      },
      formId,
    );

    if (conflict) {
      conflicts.push({
        rowId: normalizeMongoId(panelUser._id) || panelUser._id,
        email,
        name:
          panelUser.user?.full_name ||
          panelUser.full_name ||
          panelUser.panelUser?.name,
        reason: conflict,
      });
      return;
    }

    eligibleEmails.push(email);
    eligibleUsers.push({
      rowId: normalizeMongoId(panelUser._id) || panelUser._id,
      email,
      name:
        panelUser.user?.full_name ||
        panelUser.full_name ||
        panelUser.panelUser?.name ||
        email,
    });
  });

  return { conflicts, eligibleEmails, eligibleUsers, formId };
};

export const calculatePanelUserStats = (panelUsers, stats) => {
  const panelUserData = normalizePanelUsersListResponse(panelUsers);

  return {
    total: panelUserData.length,
    active: panelUserData.filter(
      (user) => (user.status || "active") === "active",
    ).length,
    assigned: panelUserData.reduce(
      (count, user) => count + (user.assigned_forms?.length || 0),
      0,
    ),
    completed: panelUserData.reduce(
      (count, user) => count + (user.completed_forms?.length || 0),
      0,
    ),
    completedFromStats: stats?.data?.complete || 0,
    incompleteFromStats: stats?.data?.incomplete || 0,
  };
};

export const buildEditPanelUserData = (record) => ({
  _id: record._id,
  status: record.status || "active",
  panelUser: record.panelUser,
  assignedForms: record.assignedForms || [],
  completedForms: record.completedForms || [],
});

export const buildCreatePanelUserPayload = ({ name, email }) => ({
  user: {
    full_name: name.trim(),
    email: email.trim(),
  },
  status: "active",
  assigned_forms: [],
  completed_forms: [],
  date_created: new Date().toISOString(),
});

export const normalizePanelUserRespondentsListResponse = (respondents) => {
  const root = respondents?.data ?? respondents;

  if (!root) {
    return [];
  }

  if (Array.isArray(root)) {
    return root;
  }

  if (Array.isArray(root.data)) {
    return root.data;
  }

  if (Array.isArray(root.respondents)) {
    return root.respondents;
  }

  if (Array.isArray(root.users)) {
    return root.users;
  }

  return [];
};

export const mapRespondentsToImportRows = (respondents) =>
  normalizePanelUserRespondentsListResponse(respondents)
    .map((respondent, index) => {
      const email =
        respondent.user?.email ||
        respondent.email ||
        respondent.user?.email_address;

      return {
        _id:
          normalizeMongoId(respondent._id) ||
          normalizeMongoId(respondent.user?._id) ||
          normalizeMongoId(respondent.id) ||
          `respondent-${index}`,
        full_name:
          respondent.user?.full_name ||
          respondent.full_name ||
          respondent.name ||
          "",
        email: email || "",
      };
    })
    .filter((row) => row.email);

export const buildImportPanelUserPayloads = (rows) =>
  rows.map((row) =>
    buildCreatePanelUserPayload({
      name:
        String(row.full_name || "").trim() === "N/A"
          ? ""
          : String(row.full_name || "").trim(),
      email: String(row.email || "").trim(),
    }),
  );

export const getPanelUserEmailSet = (panelUsers) => {
  const panelUserData = normalizePanelUsersListResponse(panelUsers);

  return new Set(
    panelUserData
      .map(
        (panelUser) =>
          panelUser.user?.email ||
          panelUser.email ||
          panelUser.user?.email_address,
      )
      .filter(Boolean)
      .map((email) => String(email).trim().toLowerCase()),
  );
};

const escapeCsvCell = (cell) => {
  if (cell == null) {
    return "";
  }

  const text = String(cell);

  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }

  return text;
};

export const buildPanelUsersCsvRows = (panelUsers) => {
  const rows = mapPanelUsersToTableData(panelUsers);

  return [
    ["Name", "Email"],
    ...rows.map((row) => [
      row.panelUser?.name || "",
      row.panelUser?.email || "",
    ]),
  ];
};

export const exportPanelUsersToCsv = (panelUsers) => {
  const csvRows = buildPanelUsersCsvRows(panelUsers);

  if (csvRows.length <= 1) {
    return { ok: false, reason: "empty" };
  }

  const csvContent = csvRows
    .map((row) => row.map(escapeCsvCell).join(","))
    .join("\n");
  const blob = new Blob(["\uFEFF" + csvContent], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const filename = `panel_users_${moment().format("YYYY-MM-DD")}.csv`;

  link.href = url;
  link.setAttribute("download", filename);
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();

  window.setTimeout(() => {
    if (link.parentNode) {
      document.body.removeChild(link);
    }
    URL.revokeObjectURL(url);
  }, 1000);

  return { ok: true, count: csvRows.length - 1 };
};
